/**
 * RunJourney — the customer-facing REDUCED history: a short vertical timeline
 * of business milestones ("Reviewed your request", "Asked you a question",
 * "Delivered") derived from the same adapted TraceEvent stream the theater
 * consumes. This intentionally replaces the engineering trace in the portal —
 * no tool ids, no payloads, no token counts.
 *
 * Live runs pulse the current step; finished runs read as a compact recap.
 * Timestamps are wall-clock (business users think in "14:32", not offsets)
 * and grow a date prefix when a HITL pause carries the run across days.
 */

import type { TraceEvent } from '@evt/flow-stream-ui'
import { Icon } from '@evt/flow-stream-ui'
import { useMemo } from 'react'
import { useI18n } from '../i18n'
import { eventsToJourney } from './journey'

export function RunJourney({
  events,
  live,
  truncated = 0
}: {
  events: TraceEvent[]
  live: boolean
  /** Count of earlier events trimmed from the persisted record, if any. */
  truncated?: number
}) {
  const { t, formatDate, formatDateTime, formatTime } = useI18n()
  const steps = useMemo(() => eventsToJourney(events, { live, strings: t.journey }), [events, live, t.journey])

  if (steps.length === 0 && !live) return null

  const multiDay = steps.length > 1 && spansDays(steps[0]?.atMs, steps[steps.length - 1]?.atMs)

  return (
    <section className="journey-card card" aria-label={t.run.historyAria}>
      <header className="journey-head">
        <h2 className="journey-title">{live ? t.run.progressTitle : t.run.howItWent}</h2>
        {live && steps.length === 0 && <span className="muted journey-hint">{t.run.waitingFirstUpdate}</span>}
      </header>

      <ol className="journey">
        {steps.map((s) => (
          <li key={s.id} className={`journey-step is-${s.status}`}>
            <span className="journey-node" aria-hidden="true">
              <Icon name={s.icon} size={13} />
            </span>
            <div className="journey-body">
              <div className="journey-row">
                <span className="journey-step-title">{s.title}</span>
                {s.chips && s.chips.length > 0 && (
                  <span className="journey-chips">
                    {s.chips.map((c, i) => (
                      <span key={`${i}:${c}`} className="journey-chip">
                        {c}
                      </span>
                    ))}
                  </span>
                )}
              </div>
              {s.detail && <p className="journey-detail">{s.detail}</p>}
            </div>
            <time
              className="journey-time"
              dateTime={new Date(s.atMs).toISOString()}
              title={`${formatDate(s.atMs)} ${formatTime(s.atMs)}`}
            >
              {multiDay ? formatDateTime(s.atMs) : formatTime(s.atMs)}
            </time>
          </li>
        ))}
      </ol>

      {truncated > 0 && <p className="journey-foot muted">{t.run.truncatedFoot(truncated)}</p>}
    </section>
  )
}

function spansDays(a: number | undefined, b: number | undefined): boolean {
  if (a === undefined || b === undefined) return false
  return new Date(a).toDateString() !== new Date(b).toDateString()
}
